import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Container, Grid, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AuroraBackground from "../visuals/AuroraBackground";
import DashboardArt from "../visuals/DashboardArt";
import Parallax from "../motion/Parallax";
import { gradients } from "../../theme/theme";

const MotionDiv = motion.div;

const stats = [
  { value: "1M+", key: "heroStatInstalls" },
  { value: "4.8★", key: "heroStatRating" },
  { value: "99.9%", key: "heroStatUptime" },
];

export default function Hero() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 6, md: 10 },
        pb: { xs: 8, md: 14 },
      }}
    >
      <AuroraBackground />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 4 }} alignItems="center">
          <Grid item xs={12} md={6}>
            {/* Text layer moves a touch faster than the visual => parallax depth */}
            <Parallax speed={0.18}>
              <MotionDiv
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.21, 0.6, 0.27, 0.97] }}
              >
                <Chip
                  icon={<BoltRoundedIcon sx={{ fontSize: 18 }} />}
                  label={t("heroBadge")}
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    color: "primary.main",
                    bgcolor: (th) =>
                      th.palette.mode === "dark" ? "rgba(79,124,255,0.14)" : "rgba(79,124,255,0.1)",
                    border: "1px solid rgba(79,124,255,0.3)",
                    "& .MuiChip-icon": { color: "primary.main" },
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{ fontSize: { xs: "2.6rem", sm: "3.4rem", md: "4rem" }, mb: 2.5 }}
                >
                  {t("heroTitleLead")}{" "}
                  <Box
                    component="span"
                    sx={{
                      background: gradients.violet,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {t("heroTitleAccent")}
                  </Box>
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: { xs: "1.05rem", md: "1.22rem" }, maxWidth: 520, mb: 4 }}
                >
                  {t("heroSubtitle")}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    size="large"
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon />}
                    onClick={() => navigate("/apps")}
                  >
                    {t("heroCtaPrimary")}
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    startIcon={<PlayArrowRoundedIcon />}
                    onClick={() => navigate("/contacts")}
                    sx={{
                      borderColor: "divider",
                      color: "text.primary",
                      "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                    }}
                  >
                    {t("heroCtaSecondary")}
                  </Button>
                </Stack>

                <Stack direction="row" spacing={{ xs: 3, sm: 5 }} sx={{ mt: 5 }}>
                  {stats.map((s) => (
                    <Box key={s.key}>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {s.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(s.key)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </MotionDiv>
            </Parallax>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Visual layer recedes (moves slower) for the parallax separation */}
            <Parallax speed={0.42}>
              <MotionDiv
                initial={{ opacity: 0, scale: 0.92, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.21, 0.6, 0.27, 0.97] }}
                style={{ position: "relative" }}
              >
                <DashboardArt />
              </MotionDiv>
            </Parallax>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
